import { InsightPrompt, InsightType } from "@/data/interfaces/InsightType";
import adminService from "@/services/adminService";
import { useEffect, useState } from "react";
import { ErrorMessage } from "@/components/UI";
import TypeForm from "../Shared/PromptManagement/TypeForm";
import TypeList from "../Shared/PromptManagement/TypeList";
import PromptForm from "../Shared/PromptManagement/PromptForm";
import PromptList from "../Shared/PromptManagement/PromptList";
import { adminStatusBadgeClass } from "../../Shared/adminUi";

type FieldValue = string | number | File;

const InsightPrompts = () => {
  const [insightTypes, setInsightTypes] = useState<InsightType[] | undefined>();
  const [selectedType, setSelectedType] = useState<InsightType | undefined>();
  const [prompts, setPrompts] = useState<InsightPrompt[] | undefined>();

  const [editingPrompt, setEditingPrompt] = useState<
    InsightPrompt | undefined
  >();
  const [editingInsightType, setEditingInsightType] = useState<
    InsightType | undefined
  >();

  const [errorMessage, setErrorMsg] = useState<string>();

  useEffect(() => {
    loadInsightTypes();
  }, []);

  useEffect(() => {
    if (selectedType) {
      loadPrompts(selectedType.id);
    }
  }, [selectedType]);

  const loadInsightTypes = async () => {
    try {
      const types = await adminService.healthInsightType.getAll();
      setInsightTypes(types);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO GAUTI TIPŲ: ${error}`);
    }
  };

  const addNewType = async (data: Record<string, FieldValue>) => {
    try {
      setErrorMsg(undefined);
      await adminService.healthInsightType.create(String(data.name));
      loadInsightTypes();
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO PRIDĖTI NAUJO TIPO: ${error}`);
    }
  };

  const updateInsightType = async (insightType: InsightType) => {
    try {
      setErrorMsg(undefined);
      await adminService.healthInsightType.update(insightType);
      setEditingInsightType(undefined);
      loadInsightTypes();
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO ATNAUJINTI TIPĄ: ${error}`);
    }
  };

  const deleteInsightType = async (type: InsightType) => {
    try {
      setErrorMsg(undefined);
      await adminService.healthInsightType.delete(type.id);
      loadInsightTypes();
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO IŠTRINTI TIPO: ${error}`);
    }
  };

  const loadPrompts = async (typeId: number) => {
    try {
      const promptsData =
        await adminService.healthInsightPrompt.getByType(typeId);
      setPrompts(promptsData);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO GAUTI PROMPTŲ: ${error}`);
    }
  };

  const addNewPrompt = async (name: string, content: string) => {
    if (!selectedType) return;

    try {
      setErrorMsg(undefined);
      await adminService.healthInsightPrompt.create(
        selectedType.id,
        name,
        content,
      );
      loadPrompts(selectedType.id);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO PRIDĖTI NAUJO PROMPTO: ${error}`);
    }
  };

  const updatePrompt = async (prompt: InsightPrompt) => {
    try {
      setErrorMsg(undefined);
      await adminService.healthInsightPrompt.update(prompt);
      setEditingPrompt(undefined);

      if (!selectedType) return;

      loadPrompts(selectedType.id);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO ATNAUJINTI PROMPTĄ: ${error}`);
    }
  };

  const deletePrompt = async (prompt: InsightPrompt) => {
    try {
      setErrorMsg(undefined);
      await adminService.healthInsightPrompt.delete(prompt.id);

      if (!selectedType) return;

      loadPrompts(selectedType.id);
    } catch (error) {
      console.error(error);
      setErrorMsg(`NEPAVYKO IŠTRINTI PROMPTĄ: ${error}`);
    }
  };

  return (
    <>
      <div className="flex flex-col-reverse gap-8 md:flex-row md:gap-4">
        <div className="flex flex-col gap-2 md:w-fit md:min-w-[200px] lg:min-w-[350px]">
          {!selectedType ? (
            <TypeForm
              onSubmit={addNewType}
              onUpdate={updateInsightType}
              editingType={editingInsightType}
              onCancelEdit={() => setEditingInsightType(undefined)}
              title="Pridėti naują tipą:"
            />
          ) : (
            <PromptForm
              selectedType={selectedType}
              onSubmit={addNewPrompt}
              onUpdate={updatePrompt}
              onBack={() => setSelectedType(undefined)}
              editingPrompt={editingPrompt}
              onCancelEdit={() => setEditingPrompt(undefined)}
            />
          )}

          <ErrorMessage error={errorMessage} />
        </div>

        {!selectedType ? (
          <TypeList
            types={insightTypes}
            onSelectType={setSelectedType}
            onDeleteType={deleteInsightType}
            onEditType={setEditingInsightType}
            title="Health Insight Tipai"
            renderTypeBadges={(type) => (
              <span className={adminStatusBadgeClass(!!type.is_active)}>
                {type.is_active ? "Aktyvus" : "Neaktyvus"}
              </span>
            )}
          />
        ) : (
          <PromptList
            prompts={prompts}
            selectedType={selectedType}
            onDeletePrompt={deletePrompt}
            onEditPrompt={setEditingPrompt}
            title="Health Insight Prompts"
            renderPromptBadges={(prompt) => (
              <span className={adminStatusBadgeClass(!!prompt.is_active)}>
                {prompt.is_active ? "Aktyvus" : "Neaktyvus"}
              </span>
            )}
          />
        )}
      </div>
    </>
  );
};

export default InsightPrompts;
